import { z } from 'zod';

/**
 * Liste exhaustive des actions sensibles tracées par le journal d'audit.
 * Toute nouvelle action sensible doit être ajoutée ici pour être typée
 * de bout en bout (back service + table audit_actions + UI).
 */
export const auditActionTypeSchema = z.enum([
  'staff.created',
  'staff.updated',
  'staff.deleted',
  'staff.deactivated',
  'staff.reactivated',
  'project.status_changed',
  'project.technician_assigned',
  'project.technician_unassigned',
  'client.updated',
  'appointment.created',
  'report.created',
  'document.reviewed',
  'payment.confirmed_card',
  'payment.validated_transfer',
  'payment.sync_from_fam',
  'pack.updated',
  'partner.created',
  'partner.profile_updated',
  'partner.compliance_reviewed',
  'lead.created',
  'lead.taken',
  'lead.converted',
  'lead.rejected',
  'intervention.assigned',
  'intervention.report_submitted',
  'intervention.reviewed',
  'partner_invoice.uploaded',
  'partner_invoice.paid',
  'appointment.requested',
  'appointment.slots_proposed',
  'appointment.slots_rejected',
  'appointment.confirmed',
  'appointment.assigned',
  'appointment.responded',
  'appointment.completed',
  'user.updated',
  'user.deactivated',
  'user.reactivated',
  'user.impersonated',
]);
export type AuditActionType = z.infer<typeof auditActionTypeSchema>;

export const auditActionEntityTypeSchema = z.enum([
  'user',
  'project',
  'client',
  'document',
  'payment',
  'pack',
  'appointment',
  'report',
  'partner',
  'lead',
  'intervention',
  'partner_invoice',
]);
export type AuditActionEntityType = z.infer<typeof auditActionEntityTypeSchema>;

/**
 * Forme renvoyée par GET /api/admin/audit : la ligne brute enrichie du nom
 * et de l'email de l'acteur (jointure users côté service).
 */
export const auditActionSchema = z.object({
  id: z.string().uuid(),
  actorId: z.string().uuid().nullable(),
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  action: auditActionTypeSchema,
  entityType: auditActionEntityTypeSchema,
  entityId: z.string().uuid().nullable(),
  payload: z.record(z.unknown()),
  createdAt: z.string(),
});
export type AuditAction = z.infer<typeof auditActionSchema>;

/**
 * Filtres acceptés par l'endpoint d'audit. Les bornes de date sont en ISO 8601.
 * `limit` est borné à 200 pour éviter d'exfiltrer toute la table en un appel.
 */
export const listAuditActionsQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  actorId: z.string().uuid().optional(),
  entityType: auditActionEntityTypeSchema.optional(),
  action: auditActionTypeSchema.optional(),
  limit: z.coerce.number().int().positive().max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListAuditActionsQuery = z.infer<typeof listAuditActionsQuerySchema>;
